import { PRODUCT_VIEW_MODE } from '../constants/viewModes.js'
import { getEntranceRefTierFromIdCad } from '../../../shared/utils/idCadUtils.js'

// ── Null object — returned before config is available ─────────────────────────

const NULL_CAPABILITIES = {
  allowsThreeDVariant: false,
  availableLevels: [],
  datasetHasProductWithAxonometric: false,
  datasetHasProductWithCompartmentVariants: false,
  datasetHasProductWithHotspots: false,
  datasetHasProductWithInsolation: false,
  defaultLevel: null,
  defaultMode: PRODUCT_VIEW_MODE.INFO,
  enabledModes: [PRODUCT_VIEW_MODE.INFO],
  hasArrangeCommissioned: false,
  hasAxonometricCamera: false,
  hasAxonometricCommissioned: false,
  hasCompartments: false,
  hasCompartmentVariants: false,
  hasHotspotCameras: false,
  hasHotspotCommissioned: false,
  hasInsolationCommissioned: false,
  hasInsolationData: false,
  hasMonetaryObligations: false,
  hasPriceHistory: false,
  hasPricing: false,
  hasRelatedProducts: false,
  hasResources: false,
  hasSupplements: false,
  hasViewer: false,
  isMultiLevel: false,
  preferThreeD: false,
  productCategory: "",
  supportsLevelSelector: false,
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

function asArray(value) {
  return Array.isArray(value) ? value : []
}

function hasSunRanges(value) {
  return asArray(value).some((entry) => Array.isArray(entry) && entry.length === 2)
}

function hasNamedCamera(tiers, matcher) {
  return asArray(tiers).some((tier) =>
    asArray(tier?.cameras).some((camera) => matcher(String(camera?.name ?? '').toLowerCase())),
  )
}

function hasHotspotCameraAssets(tiers, interiorsData) {
  const interiors = interiorsData && typeof interiorsData === 'object' ? interiorsData : {}

  return asArray(tiers).some((tier) =>
    asArray(tier?.cameras).some((camera) => {
      const cameraName = String(camera?.name ?? '')
      if (!cameraName.toLowerCase().startsWith('interior_')) return false

      const files = interiors[cameraName]
      return Array.isArray(files) && files.length > 0
    }),
  )
}

function getProductCompartments(product) {
  return asArray(product?.compartments)
}

function getCompartmentVersionsCount(compartment) {
  const raw = compartment?.versions ?? compartment?.varsions ?? 0
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

function hasCompartmentVariants(product) {
  return getProductCompartments(product).some((compartment) => getCompartmentVersionsCount(compartment) > 1)
}

function hasAnyProductCompartmentVariants(products) {
  return asArray(products).some((item) => hasCompartmentVariants(item))
}

function hasInsolationData(product) {
  if (hasSunRanges(product?.sun_light_ranges)) return true

  const tiersHasSun = asArray(product?.tiers).some((tier) => {
    const windows = asArray(tier?.windows)
    return windows.some((windowItem) => hasSunRanges(windowItem?.sun_light_ranges))
  })
  if (tiersHasSun) return true

  return getProductCompartments(product).some((compartment) => hasSunRanges(compartment?.sun_light_ranges))
}

function getLevelsCount(product, tiers) {
  const parsed = Number(product?.levels)
  if (Number.isFinite(parsed) && parsed > 0) return parsed
  return tiers.length
}

function deriveAvailableLevelsFromTiers(tiers) {
  const seen = new Set()
  const out = []

  for (const tier of tiers) {
    const refTier = Number(tier?.ref_tier)
    if (!Number.isFinite(refTier)) continue
    if (seen.has(refTier)) continue
    seen.add(refTier)
    out.push(refTier)
  }

  return out.sort((a, b) => a - b)
}

/**
 * buildDatasetCapabilities — checks if ANY product in the dataset has data for each mode.
 *
 * This is the dataset-level gate: if no product in the entire catalog supports a mode,
 * the mode won't appear in UI at all (even as disabled).
 *
 * @param {object[]} allProducts
 * @param {Record<string, unknown[]>} interiorsData
 * @returns {object}
 */
function buildDatasetCapabilities(allProducts, interiorsData) {
  return {
    datasetHasProductWithInsolation: asArray(allProducts).some((p) => hasInsolationData(p)),
    datasetHasProductWithHotspots: asArray(allProducts).some((p) => hasHotspotCameraAssets(p?.tiers, interiorsData)),
    datasetHasProductWithAxonometric: asArray(allProducts).some((p) => hasNamedCamera(p?.tiers, (name) => name === 'axonometric')),
    datasetHasProductWithCompartmentVariants: hasAnyProductCompartmentVariants(allProducts),
  }
}

// ── Pure derivation ────────────────────────────────────

/**
 * Derives Product domain capabilities based on config, settings, and product data.
 * Pure function — used by `useProductCapabilities` and unit tests.
 *
 * @param {object} [params]
 * @param {object|null} [params.projectConfig=null] - Configuration object from panel-config.json
 * @param {object|null} [params.panelSettings=null] - Configuration object from panel-settings.json
 * @param {object|null} [params.product=null] - Active product object
 * @param {string|null} [params.productCategory=null] - Business category identifier
 * @param {object[]} [params.allProducts=[]] - Full catalog products for dataset capability checks
 * @param {Record<string, unknown[]>} [params.interiorsData={}] - Global interiors assets by camera name
 * @returns {{
 *   allowsThreeDVariant: boolean,
 *   availableLevels: number[],
 *   datasetHasProductWithAxonometric: boolean,
 *   datasetHasProductWithCompartmentVariants: boolean,
 *   datasetHasProductWithHotspots: boolean,
 *   datasetHasProductWithInsolation: boolean,
 *   defaultLevel: number|null,
 *   defaultMode: string,
 *   enabledModes: string[],
 *   hasArrangeCommissioned: boolean,
 *   hasAxonometricCamera: boolean,
 *   hasAxonometricCommissioned: boolean,
 *   hasCompartments: boolean,
 *   hasCompartmentVariants: boolean,
 *   hasHotspotCameras: boolean,
 *   hasHotspotCommissioned: boolean,
 *   hasInsolationCommissioned: boolean,
 *   hasInsolationData: boolean,
 *   hasMonetaryObligations: boolean,
 *   hasPriceHistory: boolean,
 *   hasPricing: boolean,
 *   hasRelatedProducts: boolean,
 *   hasResources: boolean,
 *   hasSupplements: boolean,
 *   hasViewer: boolean,
 *   isMultiLevel: boolean,
 *   preferThreeD: boolean,
 *   productCategory: string|null,
 *   supportsLevelSelector: boolean,
 * }} Product capabilities object
 */
export function deriveProductCapabilities({
  projectConfig = null,
  panelSettings = null,
  product = null,
  productCategory = null,
  allProducts = [],
  interiorsData = {},
} = {}) {

  if (!projectConfig?.app_config || !product) {
    return NULL_CAPABILITIES;
  }

  const appConfig = projectConfig.app_config ?? {}

  const tiers = asArray(product?.tiers)
  const compartments = getProductCompartments(product)
  const levelsCount = getLevelsCount(product, tiers)
  const availableLevels = deriveAvailableLevelsFromTiers(tiers)
  const entranceLevel = getEntranceRefTierFromIdCad(product?.id_cad)
  const defaultLevel = availableLevels.includes(entranceLevel) ? entranceLevel : (availableLevels[0] ?? null)

  const hasViewer = tiers.length > 0
  const hasCompartments = compartments.length > 0

  // Project-level commissioning flags (from app_config)
  const hasInsolationCommissioned = appConfig.apartments?.floorplan_insolation === true //TODO: move to catalogCapabilities
  const hasArrangeCommissioned = appConfig.apartments?.compartments_versioning === true //TODO: move to catalogCapabilities
  const hasAxonometricCommissioned = appConfig.apartments?.render_360?.enabled === true //TODO: move to catalogCapabilities
  const hasHotspotCommissioned = appConfig.interiors?.render_ai?.enabled === true //TODO: move to catalogCapabilities
  
  // Dataset-level checks (ANY product in catalog)
  const datasetCaps = buildDatasetCapabilities(allProducts, interiorsData)

  const hasPricing = projectConfig?.website?.prices_visible ?? true;
  const hasPriceHistory = panelSettings?.additional_unit_information?.unit_price_history?.is_visible ?? false;
  const hasSupplements = panelSettings?.additional_unit_information?.supplement_purchases_options?.is_visible ?? false;
  const hasMonetaryObligations = panelSettings?.additional_unit_information?.additional_monetary_obligations?.is_visible ?? false;
  const hasResources = panelSettings?.additional_resources?.is_visible ?? true;
  const allowsThreeDVariant = appConfig.apartments?.render_top?.enabled === true &&
    (panelSettings?.unit_sheet_viewer?.render_top ?? panelSettings?.unit_sheet_viewer?.load_with_render_top) === true;
  const preferThreeD = panelSettings?.unit_sheet_viewer?.load_with_render_top === true;

  return {
    allowsThreeDVariant,
    availableLevels,
    ...datasetCaps,
    defaultLevel,
    defaultMode: PRODUCT_VIEW_MODE.INFO,
    enabledModes: [
      PRODUCT_VIEW_MODE.INFO,
      PRODUCT_VIEW_MODE.HOTSPOT,
      PRODUCT_VIEW_MODE.INSOLATION,
      PRODUCT_VIEW_MODE.ARRANGE,
      PRODUCT_VIEW_MODE.AXONOMETRIC,
    ],
    hasArrangeCommissioned,
    hasAxonometricCamera: hasNamedCamera(tiers, (name) => name === 'axonometric'),
    hasAxonometricCommissioned,
    hasCompartments,
    hasCompartmentVariants: hasCompartmentVariants(product),
    hasHotspotCameras: hasHotspotCameraAssets(tiers, interiorsData),
    hasHotspotCommissioned,
    hasInsolationCommissioned,
    hasInsolationData: hasInsolationData(product),
    hasMonetaryObligations,
    hasPriceHistory,
    hasPricing,
    hasRelatedProducts: false, // Placeholder for future implementation
    hasResources,
    hasSupplements,
    hasViewer,
    isMultiLevel: levelsCount > 1,
    preferThreeD,
    productCategory,
    supportsLevelSelector: levelsCount > 1 && availableLevels.length > 1,
  }
}
